import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronLeft, ChevronRight, FileText, LogOut } from 'lucide-react';
import { BRAND } from '../../branding/brandAssets';
import { useAuth } from '../../contexts/AuthContext';
import { filterVmsNavLinkForUser } from '../../constants/vmsAccessMatrix';
import { useVmsWorkspace } from '../../contexts/VmsWorkspaceContext';
import { resolveVmsWorkspaceParentNavHref } from '../../utils/vmsWorkspaceParentNav';
import { vmsVendorInitiationDisplayLabel, VMS_UNTITLED_SERVICE_LABEL } from '../../utils/vmsServiceDisplayName';
import type { User } from '../../types';
import type { AppShellTopBarUser } from '../AppShellTopBarAccount';
import { PlatformSwitcher } from '../PlatformSwitcher';
import { shouldShowPlatformSwitcher } from '../../utils/platformSwitch';
import { VmsAdminConfigSectionNav } from './VmsAdminConfigSectionNav';
import {
  type VmsNavChild,
  type VmsNavItem,
  ONGOING_MANAGEMENT_LABEL,
  isNavLinkActiveLocation,
  isManagementFlyoutRouteActive,
  isOngoingManagementRouteActive,
  navGroupChildActive,
  partitionVmsNavigation,
} from './vmsNavModel';

function VmsHeaderAccount({
  user,
  onLogoutClick,
}: {
  user: AppShellTopBarUser;
  onLogoutClick: () => void;
}) {
  const rootRef = useRef<HTMLDetailsElement | null>(null);

  useEffect(() => {
    const onPointerDown = (e: PointerEvent) => {
      const root = rootRef.current;
      if (!root || root.contains(e.target as Node)) return;
      root.open = false;
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, []);

  return (
    <details ref={rootRef} className="vms-header-account">
      <summary className="vms-header-account__summary">
        <span className="vms-header-account__avatar" aria-hidden>
          <span aria-hidden="true">
            {user?.first_name?.[0]}
            {user?.last_name?.[0]}
          </span>
        </span>
        <span className="sr-only">Account menu</span>
      </summary>
      <div className="vms-header-account__panel" role="menu">
        <div className="vms-header-account__identity px-3 py-2 border-b border-gray-100">
          <div className="truncate text-sm font-semibold text-gray-900">
            {user?.first_name} {user?.last_name}
          </div>
          <div className="truncate text-xs text-gray-500">{user?.email}</div>
        </div>
        <button
          type="button"
          role="menuitem"
          className="vms-header-account__logout flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
          onClick={() => {
            onLogoutClick();
          }}
        >
          <LogOut className="h-4 w-4 shrink-0 opacity-70" aria-hidden />
          Logout
        </button>
      </div>
    </details>
  );
}

function VmsServicePill() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    workspacePickerVisible,
    selectedVendorInitiationId,
    setSelectedVendorInitiationId,
    selectVendorInitiation,
    approvedOptions,
    optionsLoading,
    cachedSummary,
  } = useVmsWorkspace();

  const handleServiceChange = useCallback(
    async (raw: string) => {
      const previousId = selectedVendorInitiationId;
      const nextId = raw === '' ? null : parseInt(raw, 10);
      if (nextId != null && (!Number.isFinite(nextId) || nextId < 1)) return;

      const selectionChanged = nextId !== previousId;
      if (nextId == null) {
        setSelectedVendorInitiationId(null);
      } else {
        await selectVendorInitiation(nextId);
      }

      if (!selectionChanged) return;

      const parentHref = resolveVmsWorkspaceParentNavHref(location.pathname, location.search);
      if (parentHref) {
        navigate(parentHref, { replace: true });
      }
    },
    [
      selectedVendorInitiationId,
      setSelectedVendorInitiationId,
      selectVendorInitiation,
      location.pathname,
      location.search,
      navigate,
    ],
  );

  if (!workspacePickerVisible) return null;

  const optionIds = new Set(approvedOptions.map((o) => o.id));
  const orphanId =
    selectedVendorInitiationId != null && !optionIds.has(selectedVendorInitiationId)
      ? selectedVendorInitiationId
      : null;
  const orphanLabel =
    orphanId != null && cachedSummary?.id === orphanId
      ? vmsVendorInitiationDisplayLabel(cachedSummary.service_name, VMS_UNTITLED_SERVICE_LABEL)
      : orphanId != null
        ? VMS_UNTITLED_SERVICE_LABEL
        : null;

  return (
    <div className="vms-service-pill">
      <label htmlFor="vms-workspace-service-header" className="sr-only">
        Active vendor initiation service
      </label>
      <span className="vms-service-pill__label">Service</span>
      <span className="vms-service-pill__divider" aria-hidden />
      <select
        id="vms-workspace-service-header"
        value={selectedVendorInitiationId ?? ''}
        disabled={optionsLoading && approvedOptions.length === 0}
        onChange={(e) => {
          void handleServiceChange(e.target.value);
        }}
        className="vms-service-pill__select focus:outline-none focus:ring-0"
      >
        <option value="">All Services (no filter)</option>
        {orphanId != null ? <option value={orphanId}>{orphanLabel}</option> : null}
        {approvedOptions.map((o) => (
          <option key={o.id} value={o.id}>
            {vmsVendorInitiationDisplayLabel(o.service_name, VMS_UNTITLED_SERVICE_LABEL)}
          </option>
        ))}
      </select>
    </div>
  );
}

export function VmsTopBrandRow({
  homeHref,
  user,
  onLogoutClick,
}: {
  homeHref: string;
  user: AppShellTopBarUser;
  onLogoutClick: () => void;
}) {
  const { user: authUser } = useAuth();
  const showSwitcher = shouldShowPlatformSwitcher(authUser);

  return (
    <div className="vms-app-header__brand-row">
      {showSwitcher ? (
        <div className="vms-app-header__brand vms-app-header__brand--with-switcher">
          <PlatformSwitcher currentPlatform="vms" variant="vms-header" />
          <span className="vms-app-header__brand-divider" aria-hidden />
          <Link to={homeHref} className="vms-app-header__title-link">
            <span className="vms-app-header__logo-mark" aria-hidden>
              <img
                src={BRAND.vms.icon72}
                alt=""
                width={40}
                height={40}
                className="vms-app-header__logo-img"
                decoding="async"
              />
            </span>
            <span className="vms-app-header__title">Vendor Management System</span>
          </Link>
        </div>
      ) : (
        <Link to={homeHref} className="vms-app-header__brand">
          <span className="vms-app-header__logo-mark" aria-hidden>
            <img
              src={BRAND.vms.icon72}
              alt=""
              width={40}
              height={40}
              className="vms-app-header__logo-img"
              decoding="async"
            />
          </span>
          <span className="vms-app-header__title">Vendor Management System</span>
        </Link>
      )}
      <div className="vms-app-header__toolbar">
        <VmsServicePill />
        <VmsHeaderAccount user={user} onLogoutClick={onLogoutClick} />
      </div>
    </div>
  );
}

function closeVmsPrimaryNavFlyouts() {
  document.querySelectorAll<HTMLDetailsElement>('details[name="vms-primary-nav"]').forEach((el) => {
    el.open = false;
  });
}

/**
 * The nav clusters live inside a horizontal scroll container, which would clip the
 * dropdown panels. Pin each open panel with `position: fixed` (escapes the scroller's
 * overflow) anchored under its trigger, and keep it in the viewport.
 */
function positionVmsFlyoutPanel(details: HTMLDetailsElement) {
  const panel = details.querySelector<HTMLElement>('.vms-primary-nav__flyout-panel');
  const trigger = details.querySelector<HTMLElement>('.vms-primary-nav__flyout-trigger');
  if (!panel) return;
  if (!details.open || !trigger) {
    panel.style.removeProperty('position');
    panel.style.removeProperty('top');
    panel.style.removeProperty('left');
    return;
  }
  const rect = trigger.getBoundingClientRect();
  panel.style.position = 'fixed';
  panel.style.top = `${Math.round(rect.bottom + 2)}px`;
  const panelWidth = panel.offsetWidth || 272;
  const maxLeft = window.innerWidth - panelWidth - 8;
  const left = Math.max(8, Math.min(rect.left, Math.max(8, maxLeft)));
  panel.style.left = `${Math.round(left)}px`;
}

function repositionOpenVmsFlyouts() {
  document
    .querySelectorAll<HTMLDetailsElement>('details[name="vms-primary-nav"][open]')
    .forEach(positionVmsFlyoutPanel);
}

function clusterLinkClass(active: boolean): string {
  return [
    'vms-primary-nav__tab',
    active ? 'vms-primary-nav__tab--active' : 'vms-primary-nav__tab--inactive',
  ].join(' ');
}

function NavFlatLink({
  item,
  onNavigate,
  variant = 'tab',
}: {
  item: Extract<VmsNavItem, { type: 'link' }>;
  onNavigate?: () => void;
  variant?: 'tab' | 'menu';
}) {
  const location = useLocation();
  const active = isNavLinkActiveLocation(location, item.href);
  const Icon = item.icon;
  if (variant === 'menu') {
    return (
      <Link
        to={item.href}
        className={
          active
            ? 'vms-primary-nav__dd-link vms-primary-nav__dd-link--active'
            : 'vms-primary-nav__dd-link'
        }
        onClick={onNavigate}
        aria-current={active ? 'page' : undefined}
      >
        <Icon className="h-4 w-4 shrink-0 opacity-80" aria-hidden />
        <span>{item.name}</span>
      </Link>
    );
  }
  return (
    <Link
      to={item.href}
      className={clusterLinkClass(active)}
      onClick={onNavigate}
      aria-current={active ? 'page' : undefined}
    >
      <Icon className="vms-primary-nav__tab-icon" aria-hidden />
      <span>{item.name}</span>
    </Link>
  );
}

function NavFlyoutChildLinks({
  child,
  onNavigate,
}: {
  child: VmsNavChild;
  onNavigate?: () => void;
}) {
  const location = useLocation();
  const CIcon = child.icon;
  const parentActive = isNavLinkActiveLocation(location, child.href);
  const hasSublinks = Boolean(child.sublinks?.length);

  return (
    <div className="vms-primary-nav__flyout-child">
      <Link
        to={child.href}
        className={
          parentActive ? 'vms-primary-nav__dd-link vms-primary-nav__dd-link--active' : 'vms-primary-nav__dd-link'
        }
        onClick={onNavigate}
        aria-current={parentActive ? 'page' : undefined}
      >
        <CIcon className="h-4 w-4 shrink-0 opacity-80" aria-hidden />
        <span>{child.name}</span>
      </Link>
      {hasSublinks ? (
        <ul className="vms-primary-nav__dd-sublist">
          {child.sublinks!.map((sub) => {
            const subActive = isNavLinkActiveLocation(location, sub.href);
            return (
              <li key={`${child.name}-${sub.name}`}>
                <Link
                  to={sub.href}
                  className={
                    subActive
                      ? 'vms-primary-nav__dd-sublink vms-primary-nav__dd-sublink--active'
                      : 'vms-primary-nav__dd-sublink'
                  }
                  onClick={onNavigate}
                  aria-current={subActive ? 'page' : undefined}
                >
                  <FileText className="h-3.5 w-3.5 shrink-0 opacity-70" aria-hidden />
                  <span>{sub.name}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}

function NavGroupFlyout({
  group,
  onNavigate,
}: {
  group: Extract<VmsNavItem, { type: 'group' }>;
  onNavigate?: () => void;
}) {
  const location = useLocation();
  const active =
    group.name === ONGOING_MANAGEMENT_LABEL
      ? isOngoingManagementRouteActive(location)
      : group.children.some((c) => navGroupChildActive(location, c));

  return (
    <details
      name="vms-primary-nav"
      className="vms-primary-nav__flyout"
      data-route-active={active ? 'true' : undefined}
      onToggle={(e) => positionVmsFlyoutPanel(e.currentTarget)}
    >
      <summary
        className={[
          'vms-primary-nav__flyout-trigger',
          active ? 'vms-primary-nav__flyout-trigger--active' : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <span>{group.name}</span>
        <ChevronDown className="vms-primary-nav__chev" aria-hidden />
      </summary>
      <div className="vms-primary-nav__flyout-panel">
        {group.children.map((child) => (
          <NavFlyoutChildLinks key={child.name} child={child} onNavigate={onNavigate} />
        ))}
      </div>
    </details>
  );
}

function NavManagementFlyout({
  items,
  onNavigate,
}: {
  items: Extract<VmsNavItem, { type: 'link' }>[];
  onNavigate?: () => void;
}) {
  const location = useLocation();
  const active = isManagementFlyoutRouteActive(location, items);
  if (items.length === 0) return null;

  return (
    <details
      name="vms-primary-nav"
      className="vms-primary-nav__flyout"
      data-route-active={active ? 'true' : undefined}
      onToggle={(e) => positionVmsFlyoutPanel(e.currentTarget)}
    >
      <summary
        className={[
          'vms-primary-nav__flyout-trigger',
          active ? 'vms-primary-nav__flyout-trigger--active' : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <span>Management</span>
        <ChevronDown className="vms-primary-nav__chev" aria-hidden />
      </summary>
      <div className="vms-primary-nav__flyout-panel vms-primary-nav__flyout-panel--flat">
        {items.map((item) => (
          <NavFlatLink key={item.href} item={item} onNavigate={onNavigate} variant="menu" />
        ))}
      </div>
    </details>
  );
}

export function VmsAdminConfigNav({
  onNavigate,
  className = 'vms-admin-config-nav',
}: {
  onNavigate?: () => void;
  className?: string;
}) {
  return <VmsAdminConfigSectionNav onNavigate={onNavigate} className={className} />;
}

const REGISTER_NAV_NAMES = new Set(['Register', 'Outsourcing register']);

export function VmsHorizontalPrimaryNav({
  items,
  user,
  onNavigate,
  showAdminConfigNav = false,
}: {
  items: VmsNavItem[];
  user?: User | null;
  onNavigate?: () => void;
  showAdminConfigNav?: boolean;
}) {
  const location = useLocation();
  const navRef = useRef<HTMLElement | null>(null);
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const [overflowing, setOverflowing] = useState(false);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);
  const { top, onboarding, standalone, management, other } = useMemo(
    () => partitionVmsNavigation(items),
    [items],
  );

  const handleNavClick = useCallback(() => {
    closeVmsPrimaryNavFlyouts();
    onNavigate?.();
  }, [onNavigate]);

  const updateScrollState = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    setOverflowing(maxScroll > 1);
    setCanPrev(el.scrollLeft > 1);
    setCanNext(el.scrollLeft < maxScroll - 1);
  }, []);

  const scrollByDir = useCallback(
    (dir: 1 | -1) => {
      const el = scrollerRef.current;
      if (!el) return;
      closeVmsPrimaryNavFlyouts();
      el.scrollBy({ left: dir * Math.max(200, el.clientWidth * 0.6), behavior: 'smooth' });
    },
    [],
  );

  useEffect(() => {
    closeVmsPrimaryNavFlyouts();
  }, [location.pathname, location.search]);

  useEffect(() => {
    const onPointerDown = (e: PointerEvent) => {
      const nav = navRef.current;
      if (!nav || nav.contains(e.target as Node)) return;
      closeVmsPrimaryNavFlyouts();
    };

    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, []);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    updateScrollState();
    const onScroll = () => {
      updateScrollState();
      repositionOpenVmsFlyouts();
    };
    const onResize = () => {
      updateScrollState();
      repositionOpenVmsFlyouts();
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    const ro = new ResizeObserver(() => updateScrollState());
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      ro.disconnect();
    };
  }, [updateScrollState, items, showAdminConfigNav]);

  const dashLink = top.find((i): i is Extract<VmsNavItem, { type: 'link' }> => i.type === 'link' && i.name === 'Dashboard');
  const trayLink = top.find((i): i is Extract<VmsNavItem, { type: 'link' }> => i.type === 'link' && i.name === 'My Tray');

  const managementBundle = useMemo(() => {
    const topRemainder = top.filter(
      (i): i is Extract<VmsNavItem, { type: 'link' }> =>
        i.type === 'link' &&
        i.name !== 'Dashboard' &&
        i.name !== 'My Tray' &&
        !REGISTER_NAV_NAMES.has(i.name),
    );
    const managementLinks = management.filter(
      (i): i is Extract<VmsNavItem, { type: 'link' }> =>
        i.type === 'link' && (user ? filterVmsNavLinkForUser(user, i) : true),
    );
    const otherLinks = other.filter(
      (i): i is Extract<VmsNavItem, { type: 'link' }> =>
        i.type === 'link' && (user ? filterVmsNavLinkForUser(user, i) : true),
    );
    return [...topRemainder, ...managementLinks, ...otherLinks];
  }, [top, management, other, user]);

  const clusterPairs: React.ReactNode[] = [];
  if (dashLink) clusterPairs.push(<NavFlatLink key="dash" item={dashLink} onNavigate={handleNavClick} />);
  if (trayLink) {
    clusterPairs.push(<NavFlatLink key="tray" item={trayLink} onNavigate={handleNavClick} />);
  }

  const pinkSegments: React.ReactNode[] = [];
  standalone.forEach((item, idx) => {
    if (item.type === 'link') {
      pinkSegments.push(
        <NavFlatLink key={`vms-standalone-link-${idx}`} item={item} onNavigate={handleNavClick} variant="tab" />,
      );
    }
  });

  onboarding.forEach((item, idx) => {
    if (item.type === 'link') {
      pinkSegments.push(<NavFlatLink key={`vms-onb-link-${idx}`} item={item} onNavigate={handleNavClick} variant="tab" />);
      return;
    }
    pinkSegments.push(<NavGroupFlyout key={`vms-onb-group-${idx}`} group={item} onNavigate={handleNavClick} />);
  });

  if (managementBundle.length > 0) {
    pinkSegments.push(
      <NavManagementFlyout key="management" items={managementBundle} onNavigate={handleNavClick} />,
    );
  }

  return (
    <nav ref={navRef} className="vms-primary-nav" aria-label="Primary">
      {overflowing ? (
        <button
          type="button"
          className="vms-primary-nav__scroll-btn vms-primary-nav__scroll-btn--prev"
          onClick={() => scrollByDir(-1)}
          disabled={!canPrev}
          aria-label="Scroll navigation left"
        >
          <ChevronLeft className="vms-primary-nav__scroll-icon" aria-hidden />
        </button>
      ) : null}
      <div ref={scrollerRef} className="vms-primary-nav__scroller">
        {clusterPairs.length > 0 ? (
          <div className="vms-primary-nav__cluster">{clusterPairs}</div>
        ) : null}
        {pinkSegments.length > 0 ? (
          <div className="vms-primary-nav__cluster vms-primary-nav__cluster--secondary">{pinkSegments}</div>
        ) : null}
        {showAdminConfigNav ? (
          <div className="vms-primary-nav__cluster vms-primary-nav__cluster--admin-config">
            <VmsAdminConfigNav onNavigate={handleNavClick} className="vms-admin-config-nav" />
          </div>
        ) : null}
      </div>
      {overflowing ? (
        <button
          type="button"
          className="vms-primary-nav__scroll-btn vms-primary-nav__scroll-btn--next"
          onClick={() => scrollByDir(1)}
          disabled={!canNext}
          aria-label="Scroll navigation right"
        >
          <ChevronRight className="vms-primary-nav__scroll-icon" aria-hidden />
        </button>
      ) : null}
    </nav>
  );
}
